import { z } from "zod";
import apiClient from "../../../shared/api/client";
import type {
  ListOverlaysQuery,
  ListOverlaysResponse,
  ModerateOverlayBody,
  Overlay,
} from "../types";

const overlayCategorySchema = z.enum(["custom"]);
const overlayLicenseSchema = z.enum(["free", "paid", "community"]);
const overlayStatusSchema = z.enum(["pending", "approved", "rejected"]);

const overlaySchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  version: z.string(),
  status: overlayStatusSchema,
  category: overlayCategorySchema,
  license: overlayLicenseSchema,
  priceArs: z.number().nullable().optional(),
  authorId: z.string(),
  authorName: z.string(),
  description: z.string().nullable().optional(),
  tags: z.array(z.string()).default([]),
  installCount: z.number().default(0),
  previewThumbnailUrl: z.string().nullable().optional(),
  previewAnimatedUrl: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  template: z.unknown(),
});

const listOverlaysResponseSchema = z.object({
  items: z.array(overlaySchema),
  total: z.number(),
});

export const listOverlays = async (
  query: ListOverlaysQuery = {},
): Promise<ListOverlaysResponse> => {
  // Cola de moderación: ruta admin (todos los estados, filtro `status`). La pública
  // (`/overlays`) sólo lista approved y rechaza `status` con 400.
  const response = await apiClient.get("/admin/overlays", { params: query });
  // Backend may return either { items, total } or { data, total }; normalize.
  const raw = response.data as Record<string, unknown> | undefined;
  const candidate =
    raw && "items" in raw
      ? raw
      : { items: raw?.data ?? [], total: raw?.total ?? 0 };
  const parsed = listOverlaysResponseSchema.parse(candidate);
  return parsed as ListOverlaysResponse;
};

export const getOverlayBySlug = async (slug: string): Promise<Overlay> => {
  const response = await apiClient.get(`/overlays/${slug}`);
  const parsed = overlaySchema.parse(response.data);
  return parsed as Overlay;
};

export const moderateOverlay = async (
  id: string,
  body: ModerateOverlayBody,
): Promise<void> => {
  await apiClient.post(`/admin/overlays/${id}/moderate`, body);
};

export const overlaysApi = {
  listOverlays,
  getOverlayBySlug,
  moderateOverlay,
};
